import {
  getFirestore,
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  deleteDoc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { addMonths, getTime } from "date-fns";
import { FIRESTORE_PATH } from "../../../config/config.fireabse.firestore";
import { app } from "../FirebaseLayer";
import { INVITE_VALID_DURATION_MONTH } from "../../../config/config.invite";

// Initialize Cloud Firestore and get a reference to the service
const db = getFirestore(app);

const fstDb = {};

/**
 * @typedef {Object} InviteInfo
 * @property {number} createTime
 * @property {number} expTime
 * @property {{email: string; role: string; uid: string}} inviter
 * @property {{email: string; role: string}} invitee
 */

/**
 *
 * @param {Object} DeleteInviteInfoProp
 * @param {string} [DeleteInviteInfoProp.uid] - uid(可選)
 *
 * @returns {Promise<{success:string; error:boolean|string; data:InviteInfo | null}>}
 */
fstDb.getInviteInfo = async ({ uid }) => {
  try {
    if (!uid) {
      return {
        success: false,
        error: "uid is required for getInviteInfo()",
        data: null,
      };
    }
    if (!(typeof uid === "string")) {
      return {
        success: false,
        error: "uid must be string type for getInviteInfo()",
        data: null,
      };
    }
    const docRef = doc(db, FIRESTORE_PATH.invitation.root, uid);

    // console.log("docRef", docRef);

    /**
     *
     * @type {DocumentSnapshot<InviteInfo>}
     */
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      // console.log("Document data:", docSnap.data());
      return {
        success: true,
        error: false,
        data: docSnap.data(),
      };
    }
    // docSnap.data() will be undefined in this case
    console.log("No such document!");
    return {
      success: false,
      error: "No such document!",
      data: null,
    };
  } catch (err) {
    console.log("err", err);
    return {
      success: false,
      error: err.message,
      data: null,
    };
  }
};

// firestore/invitation 建立 invitation

/**
 * @param group {{role:string; uid:string;}}
 * @param inviter {{uid:string;}}
 * @param invitee {{email:string;}}
 * @returns {Promise<{data: {uid: string} | null; success: boolean; error: boolean}>}
 */
fstDb.postInviteInfo = async (group, inviter, invitee) => {
  try {
    if (!(group?.role && group?.uid)) {
      return {
        success: false,
        error: "group is required for postInviteInfo()",
        data: null,
      };
    }
    if (!inviter?.uid) {
      return {
        success: false,
        error: "inviter is required for postInviteInfo()",
        data: null,
      };
    }
    if (!invitee?.email) {
      return {
        success: false,
        error: "invitee is required for postInviteInfo()",
        data: null,
      };
    }
    const currentDate = new Date();
    const currentTimestamp = getTime(currentDate);
    const expDate = addMonths(currentDate, INVITE_VALID_DURATION_MONTH);
    // timestamp
    const expTimestamp = expDate.getTime();

    // Add a new document with a generated id.
    const docRef = await addDoc(
      collection(db, FIRESTORE_PATH.invitation.root),
      {
        createdTime: currentTimestamp,
        expTime: expTimestamp,
        inviter,
        group,
        invitee,
      }
    );
    // console.log("Document written with ID: ", docRef.id);

    return {
      success: true,
      error: false,
      data: {
        uid: docRef.id,
      },
    };
  } catch (err) {
    return {
      success: false,
      error: err.message,
      data: null,
    };
  }
};

/**
 *
 * @param {Object} DeleteInviteInfoProp
 * @param {string} [DeleteInviteInfoProp.uid] - uid(可選)
 *
 * @returns {Promise<{success: boolean, error}|T|{success: boolean, error: boolean}|{data: null, success: boolean, error: string}>}
 */
fstDb.deleteInviteInfo = async ({ uid }) => {
  try {
    if (!uid) {
      return {
        success: false,
        error: "uid is required for deleteInviteInfo()",
        data: null,
      };
    }
    if (!(typeof uid === "string")) {
      return {
        success: false,
        error: "uid must be string type for deleteInviteInfo()",
        data: null,
      };
    }
    const docRef = doc(db, FIRESTORE_PATH.invitation.root, uid);

    return deleteDoc(docRef)
      .then(() => ({
        success: true,
        error: false,
      }))
      .catch(() => ({
        success: false,
        error: true,
      }));
  } catch (err) {
    return {
      success: false,
      error: err.message,
    };
  }
};

/**
 * @param {string} uid
 * @param {Object} events
 * @returns {Promise<{success: boolean, error: string | boolean, data: null | Object}>}
 */
fstDb.createEvents = async (uid, events) => {
  if (!uid || typeof uid !== "string") {
    return { success: false, error: "Invalid uid", data: null };
  }
  try {
    const customEventsRef = doc(collection(db, "custom-events"), uid);
    const docSnap = await getDoc(customEventsRef);

    if (docSnap.exists()) {
      await updateDoc(customEventsRef, { events: arrayUnion(events) });
    } else {
      await setDoc(customEventsRef, { uid, events: [events] });
    }
    return { success: true, error: false, data: { uid } };
  } catch (err) {
    return { success: false, error: err.message, data: null };
  }
};

/**
 * @param {string} uid
 * @returns {Promise<{success: boolean, error: string | boolean, data: null | Object}>}
 */
fstDb.readEvents = async (uid) => {
  if (!uid) {
    return { success: false, error: "Invalid uid", data: null };
  }
  try {
    const customEventsRef = doc(collection(db, "custom-events"), uid);
    const docSnap = await getDoc(customEventsRef);

    if (docSnap.exists()) {
      return { success: true, error: false, data: docSnap.data() };
    }
    return { success: false, error: "No such document!", data: null };
  } catch (err) {
    return { success: false, error: err.message, data: null };
  }
};

/**
 * @param {string} uid
 * @param {string} evtTypeId
 * @param {Object} updateData
 * @returns {Promise<{success: boolean, error: string | boolean, data: null | Object}>}
 */
fstDb.updateEvents = async (uid, evtTypeId, updateData) => {
  if (
    !uid ||
    typeof uid !== "string" ||
    !evtTypeId ||
    typeof evtTypeId !== "string" ||
    !updateData ||
    typeof updateData !== "object"
  ) {
    return { success: false, error: "Invalid input", data: null };
  }
  try {
    const customEventsRef = doc(collection(db, "custom-events"), uid);
    const docSnap = await getDoc(customEventsRef);

    if (!docSnap.exists()) {
      return { success: false, error: "Document does not exist", data: null };
    }

    const customEvents = docSnap.data().events;
    const eventToUpdate = customEvents.find(
      (event) => event.evtTypeId === evtTypeId
    );

    if (!eventToUpdate) {
      return { success: false, error: "Event does not exist", data: null };
    }

    Object.assign(eventToUpdate, updateData);

    await updateDoc(customEventsRef, { events: customEvents });

    return { success: true, error: false, data: { uid } };
  } catch (err) {
    return { success: false, error: err.message, data: null };
  }
};

/**
 * @param {string} uid
 * @param {Array<string>} evtTypeIds
 * @returns {Promise<{success: boolean, error: string | boolean, data: null | Object}>}
 */
fstDb.deleteEvents = async (uid, evtTypeIds) => {
  if (
    !uid ||
    typeof uid !== "string" ||
    !Array.isArray(evtTypeIds) ||
    evtTypeIds.length === 0
  ) {
    return { success: false, error: "Invalid input", data: null };
  }
  try {
    const customEventsRef = doc(collection(db, "custom-events"), uid);
    const docSnap = await getDoc(customEventsRef);

    if (!docSnap.exists()) {
      return { success: false, error: "Document does not exist", data: null };
    }

    const filteredEvents = docSnap
      .data()
      .events.filter((event) => !evtTypeIds.includes(event.evtTypeId));

    await updateDoc(customEventsRef, { events: filteredEvents });

    return {
      success: true,
      error: false,
      data: { uid, deletedEvtTypeIds: evtTypeIds },
    };
  } catch (err) {
    return { success: false, error: err.message, data: null };
  }
};

export default fstDb;
